import { useMemo, useState } from "react";
import { useTellescopeInfiniteQuery, useTellescopeQuery } from "@tellescope/tanstack-query";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { CANVAS_TITLE, BRIDGE_TITLE } from "@tellescope/constants";
import type { DatabaseRecord, Enduser, FormFieldOptions } from "@tellescope/types-models";
import type { TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { TellescopeFieldResponse } from "../../types";
import { getLatestAddressAnswer } from "../../utils";

export interface Payer {
  id: string;
  name: string;
  state?: string;
  type?: string;
  databaseRecord?: DatabaseRecord;
}

const isCanvasOrgArray = (value: unknown): value is Array<{ resource: { id: string; name: string } }> => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      "resource" in item &&
      typeof item.resource === "object" &&
      item.resource !== null
  );
};

const isBridgePayerArray = (value: unknown): value is Array<{ id: string; name: string }> => {
  if (!Array.isArray(value)) return false;
  return value.every(
    (item) =>
      typeof item === "object" &&
      item !== null &&
      "id" in item &&
      "name" in item &&
      typeof item.id === "string" &&
      typeof item.name === "string"
  );
};

interface UseInsurerDropdownArgs {
  fieldId: string;
  options: FormFieldOptions | undefined;
  formResponses: TellescopeFieldResponse[];
  enduser?: TellescopeModelFromName<"endusers"> | Enduser;
}

export const useInsurerDropdown = ({ fieldId, options, formResponses, enduser }: UseInsurerDropdownArgs) => {
  const dataSource = options?.dataSource;
  const isCanvas = dataSource === CANVAS_TITLE;
  const isBridge = dataSource === BRIDGE_TITLE;
  const isDb = !isCanvas && !isBridge;

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery);

  const addressAnswer = getLatestAddressAnswer(formResponses);
  const resolvedState = addressAnswer?.state ?? enduser?.state ?? "";

  const dbQuery = useTellescopeInfiniteQuery({
    model: "form_fields",
    method: "load_choices_from_database",
    request: { fieldId, limit: 10000 },
    paginateRequest: (lastId, request) => ({ ...request, lastId }),
    initialPageParam: undefined,
    getNextPageParam: () => undefined,
    enabled: isDb && !!fieldId,
  });

  const canvasQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: CANVAS_TITLE,
      type: "organizations",
      query: debouncedSearchQuery,
    },
    enabled: isCanvas,
  });

  const bridgeQuery = useTellescopeQuery({
    model: "integrations",
    method: "proxy_read",
    request: {
      integration: BRIDGE_TITLE,
      type: "payers",
      query: debouncedSearchQuery,
    },
    enabled: isBridge,
  });

  const dbPages = dbQuery.data?.pages;
  const canvasData = canvasQuery.data?.data;
  const bridgeData = bridgeQuery.data?.data;

  const payers = useMemo<Payer[]>(() => {
    if (isDb) {
      const choices = dbPages?.flatMap((page) => page.choices) ?? [];
      return choices.reduce<Payer[]>((acc, record) => {
        const get = (key: string) => {
          const match = record.values.find((v) => v.label.trim().toLowerCase() === key);
          return typeof match?.value === "string" ? match.value : undefined;
        };
        const name = get("name");
        if (!name) return acc;
        const state = get("state");
        if (state && resolvedState && state !== resolvedState) return acc;
        acc.push({
          id: get("id") ?? record.id,
          name,
          state,
          type: get("type"),
          databaseRecord: record,
        });
        return acc;
      }, []);
    }

    if (isCanvas) {
      if (!isCanvasOrgArray(canvasData)) return [];
      return canvasData.map((d) => ({ id: d.resource.id, name: d.resource.name }));
    }

    if (isBridge) {
      if (!isBridgePayerArray(bridgeData)) return [];
      return bridgeData.map((d) => ({ id: d.id, name: d.name }));
    }

    return [];
  }, [isDb, isCanvas, isBridge, dbPages, canvasData, bridgeData, resolvedState]);

  const isLoading = isDb ? dbQuery.isLoading : isCanvas ? canvasQuery.isFetching : bridgeQuery.isFetching;

  return {
    payers,
    isLoading,
    isLiveSearch: isCanvas || isBridge,
    searchQuery,
    setSearchQuery,
  };
};
